import { useEffect, useMemo, useRef, useState } from 'react';

import i18next from 'i18next';
import { DraggableItem } from '@bringg-frontend/draggable-list-selector';
import { CustomUnitsDefinition, DynamicCapacityValuesOptions, GroupData } from '@bringg/types';
import { DetailedColumn, ViewColumn, detailsHeaders } from '@bringg-frontend/monitoring-shared';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getRoot } from 'mobx-easy';

import { hasFeatureFlag } from '../../../../../utils/feature-flags';
import { useStores } from '../../../../../recipes';
import DriverCell from '../cell-renderers/driver-cell/driver-cell';
import VehicleCell from '../cell-renderers/vehicle-cell/vehicle-cell';
import TrailerCell from '../cell-renderers/trailer-cell';
import FulfillmentCell from '../cell-renderers/fulfillment-cell/fulfillment-cell';
import DateCell from '../cell-renderers/date-cell';
import EtaBaseCell from '../cell-renderers/eta-base-cell';
import TimeCell from '../cell-renderers/time-cell';
import { columnsProvider } from '../../utils/details-columns-provider';
import { DETAILS_PANEL_SIZES_KEY } from '../details-table';
import { routesMonitoringRootStore } from '../../stores/routes-monitoring-root-store';
import RootStore from 'bringg-web/stores/root-store';

export const DETAILS_COLUMNS_KEY = 'routes-monitoring-details-columns';

const loadedUnitColumn = (handlingUnits: CustomUnitsDefinition, unit: string) => ({
	id: `loaded-${handlingUnits[unit].name}`.replaceAll(' ', '-').toLowerCase(),
	Header: `${i18next.t(detailsHeaders.loaded)} ${handlingUnits[unit].name}`,
	accessor: (row: GroupData) => {
		const route = getRoot<RootStore>().data.runStore.get(row.data.id);
		if (!route) {
			return null;
		}
		return `${route.dynamic_aggregated_handling_units?.current_load?.[unit]?.toLocaleString() || 0}`;
	}
});

const expectedUnitColumn = (handlingUnits: CustomUnitsDefinition, unit: string) => ({
	id: `expected-${handlingUnits[unit].name}`.replaceAll(' ', '-').toLowerCase(),
	Header: `${i18next.t(detailsHeaders.expected)} ${handlingUnits[unit].name}`,
	accessor: (row: GroupData) => {
		const route = getRoot<RootStore>().data.runStore.get(row.data.id);
		if (!route) {
			return null;
		}
		return `${route.dynamic_aggregated_handling_units?.expected_pickup?.[unit]?.toLocaleString() || 0}`;
	}
});

const loadedWeightColumn = () => ({
	id: 'loaded-weight',
	Header: i18next.t(detailsHeaders.loadedWeight),
	accessor: (row: GroupData) => {
		const route = getRoot<RootStore>().data.runStore.get(row.data.id);
		return `${route?.dynamic_aggregated_weight?.current_load?.toLocaleString() || 0}`;
	}
});

const expectedWeightColumn = () => ({
	id: 'expected-weight',
	Header: i18next.t(detailsHeaders.expectedWeight),
	accessor: (row: GroupData) => {
		const route = getRoot<RootStore>().data.runStore.get(row.data.id);
		return `${route?.dynamic_aggregated_weight?.expected_pickup?.toLocaleString() || 0}`;
	}
});

export interface ColumnConfigItem {
	id: string;
	required?: boolean;
}

export const defaultColumns: ColumnConfigItem[] = [
	{ id: 'driver-name', required: true },
	{ id: 'scheduled-start-time' },
	{ id: 'eta-to-base' },
	{ id: 'weight' },
	{ id: 'fulfillment' },
	{ id: 'tasks-count' }
];

export interface DetailedCellProps {
	value?: any;
	row: GroupData;
}

export const getGeneralColumns = (): DetailedColumn<GroupData, GroupData>[] => [
	{
		id: 'driver-name',
		Header: i18next.t(detailsHeaders.driver),
		Cell: DriverCell
	},
	{
		id: 'vehicle-name',
		Header: i18next.t(detailsHeaders.vehicle),
		Cell: VehicleCell
	},
	{
		id: 'trailer',
		Header: i18next.t(detailsHeaders.trailer),
		Cell: TrailerCell
	},
	{
		id: 'fulfillment',
		Header: i18next.t(detailsHeaders.fulfillment),
		Cell: FulfillmentCell
	},
	{
		id: 'started-at',
		Header: i18next.t(detailsHeaders.startedAt),
		accessor: row => {
			const route = getRoot<RootStore>().data.runStore.get(row.data.id);

			return route?.started_at || '';
		},
		Cell: DateCell
	},
	{
		id: 'ended-at',
		Header: i18next.t(detailsHeaders.endedAt),
		accessor: row => {
			const route = getRoot<RootStore>().data.runStore.get(row.data.id);
			return route?.ended_at || '';
		},
		Cell: DateCell
	},
	{
		id: 'scheduled-start-time',
		Header: i18next.t(detailsHeaders.leaveBy),
		accessor: row => {
			const route = getRoot<RootStore>().data.runStore.get(row.data.id);
			return route?.scheduled_start_time || '';
		},
		Cell: TimeCell
	},
	{
		id: 'eta-to-base',
		Header: i18next.t(detailsHeaders.etaToBase),
		Cell: EtaBaseCell
	},
	{
		id: 'tasks-count',
		Header: i18next.t(detailsHeaders.ordersCount),
		accessor: row => {
			const route = getRoot<RootStore>().data.runStore.get(row.data.id);

			return `${route?.task_ids.length || ''}`;
		}
	},
	{
		id: 'total-cost',
		Header: i18next.t(detailsHeaders.totalCost),
		accessor: row => {
			const tasksIds = routesMonitoringRootStore
				.getStore()
				.routesMonitoringStore.currentTableDefinition.itemsByGroup[row.id].map(item => item.id);
			const tasks = getRootEnv().dashboardSdk.sdk.v2.tasks.getMany(tasksIds);
			const totalCost = tasks.reduce((total, task) => {
				return total + (task.total_price || 0);
			}, 0);
			return `${totalCost || ''}`;
		}
	},
	{
		id: 'weight',
		Header: i18next.t(detailsHeaders.totalWeight),
		accessor: row => {
			const route = getRoot<RootStore>().data.runStore.get(row.data.id);
			if (!route) {
				return null;
			}
			return `${(route.max_weight || 0).toLocaleString()}/${(route.weight_capacity || 0).toLocaleString()}`;
		}
	}
];

export const useColumns = (
	groupId: string,
	columnsLayoutMap: Map<string, number>,
	onColumnsChange: (sizeRatioChange: number) => void
) => {
	const spreadEven = useRef(false);
	const {
		merchantConfigurationsStore,
		usersStore: { currentUser }
	} = useStores();
	const { custom_units_definition, dynamic_capacity_values_mode } = merchantConfigurationsStore.configuration;
	const columnConfigEnabled = hasFeatureFlag(currentUser, 'routes_monitoring_columns_config_enabled');

	const [selectedColumns, setSelectedColumns] = useState<ColumnConfigItem[]>([]);
	const [columnsToView, setColumnsToView] = useState<ViewColumn<GroupData, GroupData>[]>([]);
	const [columnsConfig, setColumnsConfig] = useState<{
		selectedValues: DraggableItem[];
		unselectedValues: DraggableItem[];
	}>({ selectedValues: [], unselectedValues: [] });

	const availableColumns = useMemo<DetailedColumn<GroupData, GroupData>[]>(() => {
		const columns = getGeneralColumns();

		const handlingUnits = custom_units_definition || {};

		Object.keys(handlingUnits).forEach(unit => {
			columns.push({
				id: handlingUnits[unit].name.replaceAll(' ', '-').toLowerCase(),
				Header: handlingUnits[unit].name,
				Cell: ({ row }: DetailedCellProps) => {
					const { runStore } = useStores();
					const route = runStore.get(row.data.id);

					return (
						<>{`${route.max_handling_units?.[unit]?.toLocaleString() || 0}/${
							route.handling_units_capacity?.[unit]?.toLocaleString() || 0
						}`}</>
					);
				}
			});
		});

		if (dynamic_capacity_values_mode != null && dynamic_capacity_values_mode !== DynamicCapacityValuesOptions.OFF) {
			switch (dynamic_capacity_values_mode) {
				case DynamicCapacityValuesOptions.ALL:
					Object.keys(handlingUnits).forEach(unit => {
						columns.push(loadedUnitColumn(handlingUnits, unit), expectedUnitColumn(handlingUnits, unit));
					});

					columns.push(loadedWeightColumn(), expectedWeightColumn());
					break;

				case DynamicCapacityValuesOptions.CURRENT_LOAD:
					Object.keys(handlingUnits).forEach(unit => {
						columns.push(loadedUnitColumn(handlingUnits, unit));
					});

					columns.push(loadedWeightColumn());
					break;

				case DynamicCapacityValuesOptions.EXPECTED_PICKUP:
					Object.keys(handlingUnits).forEach(unit => {
						columns.push(expectedUnitColumn(handlingUnits, unit));
					});

					columns.push(expectedWeightColumn());
					break;
			}
		}

		return columns;
	}, []);

	useEffect(() => {
		let userColumnConfig: ColumnConfigItem[];

		if (columnConfigEnabled) {
			userColumnConfig = columnsProvider.getUserColumnConfig(DETAILS_COLUMNS_KEY) || defaultColumns;
		} else {
			userColumnConfig = availableColumns.map(column => ({ id: column.id }));
		}

		setSelectedColumns(userColumnConfig);
	}, []);

	useEffect(() => {
		const config = columnsProvider.calculateColumnConfig(availableColumns, selectedColumns);

		setColumnsConfig(config);

		const { columns, changeRatio } = columnsProvider.calculateVisibleColumns(
			groupId,
			selectedColumns,
			availableColumns,
			columnsLayoutMap,
			spreadEven.current,
			DETAILS_PANEL_SIZES_KEY
		);

		spreadEven.current = false;

		columns.length && columnsLayoutMap.size > 0 && onColumnsChange(changeRatio);
		setColumnsToView(columns);
	}, [availableColumns, selectedColumns]);

	const getDefaultConfig = () => {
		spreadEven.current = true;
		return columnsProvider.calculateColumnConfig(availableColumns, defaultColumns);
	};

	const onSelectedColumnsChange = (columns: DraggableItem[]) => {
		const selectedConfig = columns.map(({ id, required }) => ({ id, required }));

		setSelectedColumns(selectedConfig);
		columnsProvider.saveUserColumnConfig(DETAILS_COLUMNS_KEY, selectedConfig);
	};

	return { columnsToView, columnsConfig, onSelectedColumnsChange, getDefaultConfig };
};
